import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { Weekdays, WeekdaySelectValue } from './weekday-select.model';
import { WeekdaySelectService } from './weekday-select.service';

@Component({
  selector: 'rk-weekday-select',
  templateUrl: './weekday-select.component.html',
  styleUrls: ['./weekday-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => WeekdaySelectComponent),
    },
  ],
})
export class WeekdaySelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  /**
   * Form instance
   */
  form: FormGroup;

  /**
   * Weekdays, representing possible choices as a form control
   */
  weekdays = Object.values(Weekdays).map(weekday => ({
    id: weekday,
    name: WeekdaySelectService.weekdayChoicesEnumValueToMoment(weekday).format('dd')[0],
    control: this.fb.control(true),
  }));

  subscription = new Subscription();

  constructor(private readonly fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize form (values, events)
   */
  initializeForm(): void {
    this.form = this.fb.group({
      days: this.buildDaysControls(),
    });
  }

  /**
   * Build days as form controls
   */
  buildDaysControls() {
    return this.fb.array(this.weekdays.map(day => day.control));
  }

  /**
   * As a form control, apply specified value to component
   */
  writeValue(days: WeekdaySelectValue): void {
    if (!days) {
      return;
    }

    this.weekdays.forEach(day => {
      day.control.setValue(days.has(day.id));
    });
  }

  /**
   * As a form control, emit component value when changed
   */
  registerOnChange(fn: any): void {
    this.subscription.add(
      this.form.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
        const set = new Set(
          this.weekdays.filter(day => day.control.value).map(day => day.id),
        );

        return fn(set);
      }),
    );
  }

  /**
   * Not implemented
   */
  registerOnTouched(fn: any): void {}

  /**
   * Not implemented
   */
  setDisabledState?(isDisabled: boolean): void {}
}
